import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, X, Check, FileText } from 'lucide-react';
import { accountNotes as accountNotesApi } from '../../../services/api';

// ── Constants ──────────────────────────────────────────────────────────────────
const NOTE_TYPES = ['general', 'call', 'visit', 'email', 'other'];

const NOTE_BADGE = {
  general: 'bg-secondary',
  call:    'bg-primary',
  visit:   'bg-success',
  email:   'bg-info',
  other:   'bg-warning text-dark',
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function timeAgo(dateStr) {
  const diffMs  = Date.now() - new Date(dateStr).getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr  = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDays = Math.floor(diffHr / 24);
  if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  const diffMo = Math.floor(diffDays / 30);
  return `${diffMo} month${diffMo !== 1 ? 's' : ''} ago`;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function AccountNotesPanel({ customerID, currentUserID, currentUserRole }) {
  const [notes,      setNotes]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState('');

  // Add form
  const [showForm,   setShowForm]   = useState(false);
  const [noteType,   setNoteType]   = useState('general');
  const [noteBody,   setNoteBody]   = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Inline edit
  const [editID,     setEditID]     = useState(null);
  const [editBody,   setEditBody]   = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Delete confirm modal
  const [deleteID,   setDeleteID]   = useState(null);
  const [deleting,   setDeleting]   = useState(false);

  // ── Load ────────────────────────────────────────────────────────────────────
  const loadNotes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await accountNotesApi.list(customerID);
      setNotes(res.data.data || []);
    } catch {
      setError('Failed to load notes.');
    } finally {
      setLoading(false);
    }
  }, [customerID]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  // ── Permission helpers ───────────────────────────────────────────────────────
  function canEdit(note) {
    if (currentUserRole === 'admin') return true;
    if (note.authorID !== currentUserID) return false;
    return (Date.now() - new Date(note.createdAt).getTime()) <= 15 * 60 * 1000;
  }

  const canDelete = () => currentUserRole === 'admin';

  // ── Submit new note ──────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!noteBody.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await accountNotesApi.create(customerID, { noteType, body: noteBody.trim() });
      setNotes(prev => [res.data.data, ...prev]);
      setNoteBody('');
      setNoteType('general');
      setShowForm(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add note.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Inline edit ──────────────────────────────────────────────────────────────
  const startEdit = (note) => {
    setEditID(note.noteID);
    setEditBody(note.body);
  };

  const handleSaveEdit = async () => {
    if (!editBody.trim()) return;
    setEditSaving(true);
    setError('');
    try {
      await accountNotesApi.update(customerID, editID, { body: editBody.trim() });
      setNotes(prev => prev.map(n =>
        n.noteID === editID
          ? { ...n, body: editBody.trim(), editedAt: new Date().toISOString() }
          : n
      ));
      setEditID(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update note.');
    } finally {
      setEditSaving(false);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await accountNotesApi.delete(customerID, deleteID);
      setNotes(prev => prev.filter(n => n.noteID !== deleteID));
      setDeleteID(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete note.');
    } finally {
      setDeleting(false);
    }
  };

  // ── Toggle add form ──────────────────────────────────────────────────────────
  const toggleForm = () => {
    setShowForm(f => !f);
    setNoteBody('');
    setNoteType('general');
    setError('');
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h5 className="mb-0">Activity Notes</h5>
        <button
          className="btn btn-primary btn-sm d-flex align-items-center gap-1"
          onClick={toggleForm}
        >
          <Plus size={14} />
          Add Note
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-danger py-2 d-flex align-items-center justify-content-between mb-3">
          <span>{error}</span>
          <button className="btn-close btn-sm" onClick={() => setError('')} aria-label="Dismiss" />
        </div>
      )}

      {/* Add note form */}
      {showForm && (
        <div className="card mb-3">
          <div className="card-body">
            {/* Note type toggle */}
            <div className="mb-3">
              <label className="form-label">Type</label>
              <div className="d-flex flex-wrap gap-1" role="group" aria-label="Note type">
                {NOTE_TYPES.map(t => (
                  <button
                    key={t}
                    type="button"
                    className={`btn btn-sm ${noteType === t ? 'btn-dark' : 'btn-outline-secondary'}`}
                    onClick={() => setNoteType(t)}
                  >
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            {/* Body */}
            <div className="mb-3">
              <label className="form-label">Note</label>
              <textarea
                className={`form-control ${noteBody.length > 2000 ? 'is-invalid' : ''}`}
                rows={3}
                value={noteBody}
                onChange={e => setNoteBody(e.target.value)}
                placeholder="Add a note…"
                autoFocus
              />
              <div className="d-flex justify-content-end mt-1">
                <small className={noteBody.length > 2000 ? 'text-danger fw-semibold' : 'text-muted'}>
                  {noteBody.length.toLocaleString()} / 2,000
                </small>
              </div>
            </div>
            {/* Actions */}
            <div className="d-flex gap-2">
              <button
                className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                onClick={handleSubmit}
                disabled={submitting || !noteBody.trim() || noteBody.length > 2000}
              >
                {submitting
                  ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                  : <Check size={13} />}
                Save Note
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowForm(false)}
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes list */}
      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border spinner-border-sm text-secondary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : notes.length === 0 ? (
        <div className="text-center text-muted py-5 border rounded">
          <FileText size={32} className="mb-2 opacity-50" />
          <p className="mb-0">No notes yet. Add the first note above.</p>
        </div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {notes.map(note => (
            <div key={note.noteID} className="card">
              <div className="card-body py-3">
                {/* Header row */}
                <div className="d-flex align-items-start justify-content-between mb-2">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className={`badge ${NOTE_BADGE[note.noteType] || 'bg-secondary'} text-capitalize`}>
                      {note.noteType}
                    </span>
                    <small className="text-muted">
                      <strong>{note.authorName}</strong>
                      {' · '}
                      {timeAgo(note.createdAt)}
                      {note.editedAt && ' (edited)'}
                    </small>
                  </div>
                  <div className="d-flex gap-1 ms-2 flex-shrink-0">
                    {canEdit(note) && editID !== note.noteID && (
                      <button
                        className="btn btn-outline-secondary btn-sm py-0 px-1"
                        title="Edit note"
                        onClick={() => startEdit(note)}
                      >
                        <Pencil size={12} />
                      </button>
                    )}
                    {canDelete() && (
                      <button
                        className="btn btn-outline-danger btn-sm py-0 px-1"
                        title="Delete note"
                        onClick={() => setDeleteID(note.noteID)}
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Body or inline edit */}
                {editID === note.noteID ? (
                  <div>
                    <textarea
                      className="form-control form-control-sm mb-2"
                      rows={3}
                      value={editBody}
                      onChange={e => setEditBody(e.target.value)}
                      autoFocus
                    />
                    <div className="d-flex gap-1">
                      <button
                        className="btn btn-success btn-sm py-0 px-2 d-flex align-items-center gap-1"
                        onClick={handleSaveEdit}
                        disabled={editSaving || !editBody.trim()}
                      >
                        {editSaving
                          ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          : <Check size={12} />}
                        Save
                      </button>
                      <button
                        className="btn btn-outline-secondary btn-sm py-0 px-2"
                        onClick={() => setEditID(null)}
                        disabled={editSaving}
                      >
                        <X size={12} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{note.body}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteID && (
        <>
          <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content">
                <div className="modal-body text-center py-4">
                  <Trash2 size={32} className="text-danger mb-2" />
                  <p className="mb-0 fw-semibold">Delete this note?</p>
                  <p className="text-muted small mt-1 mb-0">This action cannot be undone.</p>
                </div>
                <div className="modal-footer justify-content-center border-0 pt-0">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setDeleteID(null)}
                    disabled={deleting}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    {deleting
                      ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                      : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
            className="modal-backdrop fade show"
            onClick={() => { if (!deleting) setDeleteID(null); }}
          />
        </>
      )}
    </div>
  );
}
